import { HireFlowButton } from "@/components/employee/HireFlowButton";
import { apiEmployees } from "@/api/employees/employee.api";
import { useColumns } from "@/components/column-manager/use-columns.hook.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  DEFAULT_CATALOG_COLUMNS,
  useEmployeeColumnsCatalog,
} from "@/pages/employees/index/use-employee-columns-catalog.tsx";
import { buildLoadColumns } from "@/pages/employees/index/employee-column-values.ts";
import {
  COLUMN_FILTERS_PARAM,
  LIFECYCLE_STATUS_COLUMN,
  useEmployeeColumnFilters,
} from "@/pages/employees/index/use-employee-column-filters.ts";
import { useOrganizationViewFilter } from "@/queries/settings/use-organization.query.ts";
import {
  migrateLegacyEmployeeFilterParams,
  migrateLegacyEmployeeFilters,
} from "@/pages/employees/index/employee-filter-migration.ts";
import { EmployeeColumnFiltersModal } from "@/pages/employees/index/EmployeeColumnFiltersModal.tsx";
import { EMPLOYEE_FILTER_RENDERERS } from "@/pages/employees/index/employee-filter-value-renderers.tsx";
import {
  getClearedColumnFilters,
  getDisplayedColumnFilters,
} from "@/pages/employees/index/employee-column-filters-modal.helper.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployeeListView } from "@/types/employees/employee-view.types.ts";
import { ApiEmployeeFilter } from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  Box,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  parseUrlHelper,
  Stack,
  FilterButton,
  Table,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";
import { useEmployeesModel } from "@/pages/employees/index/use-employees-model";
import {
  ALL_EMPLOYEES_PARAM,
  useEmployeeViewActions,
  useEmployeeViewsNav,
  VIEW_PARAM,
} from "@/pages/employees/index/use-employee-views-state";
import { EMPLOYEES_LIST_REFERRER_KEY } from "@/pages/employees/employee/employee-page.constants.ts";
import { EmployeeViewSelector } from "@/pages/employees/index/EmployeeViewSelector";
import { EmployeeSaveViewButton } from "@/pages/employees/index/EmployeeSaveViewButton";
import { SaveViewModal } from "@/pages/employees/index/SaveViewModal";
import { ManageViewsModal } from "@/pages/employees/index/ManageViewsModal";
import { EmployeeColumnFiltersChip } from "@/pages/employees/index/EmployeeColumnFiltersChip.tsx";

export default function Employees() {
  const { t } = useTranslation();
  const { canIEdit, canCurrentEmployeeViewField } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("text") ?? "");
  const [savedFiltersHydrated, setSavedFiltersHydrated] = useState(false);
  const { downloadFile } = useDownloadHelper();

  useTitle(t("core:employees"));

  const {
    columnDefinitions,
    columnConfig,
    columnByKey,
    filterCategories,
    migrateSeedColumns,
    isLoading: isCatalogLoading,
  } = useEmployeeColumnsCatalog();

  const isCatalogReady = columnByKey.size > 0;

  const viewsNav = useEmployeeViewsNav(canIEdit(ApiPermission.employees_search));

  // Старые вьюхи хранят legacy-id и slug'и кастомных полей: приводим их к
  // ключам каталога, при пересохранении вьюхи запишутся уже ключами
  const seedColumns = useMemo(() => {
    const savedColumns = viewsNav.activeView?.columns?.columns;
    if (!savedColumns) return undefined;
    return migrateSeedColumns(savedColumns.map((column) => column.id)).map((id) => ({
      id,
      title: columnByKey.get(id)?.title ?? "",
    }));
  }, [viewsNav.activeView, migrateSeedColumns, columnByKey]);

  const defaultVisibleColumns = useMemo(
    () => migrateSeedColumns(DEFAULT_CATALOG_COLUMNS),
    [migrateSeedColumns]
  );

  const { columns, visibleColumns, columnsButton, currentColumns } = useColumns({
    columnConfig,
    defaultVisibleColumns,
    columnDefinitions,
    seedColumns,
    // Пока каталог не пришёл, миграция ключей вернёт пустой список: пересеиваем
    // один раз по факту его готовности, а не при каждом изменении состава —
    // иначе выбор пользователя сбросится при повторной загрузке каталога
    seedColumnsKey: `${viewsNav.activeViewId ?? "default"}:${isCatalogReady}`,
    source: "Employees Columns",
  });

  const { employeesData, isEmployeesLoading, updateFilters } = useEmployeesModel();

  const canSearch = canIEdit(ApiPermission.employees_search);

  const canViewEmployeeName = canCurrentEmployeeViewField(EmployeeFieldPermission.employee_name);

  const library = filtersLibrary();

  const employeesFilterConfig = useMemo(
    () => [library.page_number, { ...library.text, searchParamName: "text" }, { ...library.sort }],
    [library]
  );

  const {
    filter: { filter, typedFilters },
    onApplySorting,
    isFilterReady,
  } = useAppFilter<ApiEmployeeFilter>({
    filters: employeesFilterConfig,
    source: "Employees",
  });

  const columnFilters = useEmployeeColumnFilters(columnByKey);

  // Статусы организации по умолчанию: панель показывает их, пока статус не задан явно
  const lifecycleColumn = columnByKey.get(LIFECYCLE_STATUS_COLUMN);
  const { data: organizationViewFilter } = useOrganizationViewFilter(
    canSearch && Boolean(lifecycleColumn?.is_filterable)
  );
  const lifecycleStatusDefaults = organizationViewFilter?.employee_lifecycle_statuses;

  // Счётчик и чип учитывают статусы по умолчанию: они сужают список, пока статус не задан явно
  const displayedColumnFilters = useMemo(
    () =>
      getDisplayedColumnFilters(
        columnFilters.activeFilters,
        lifecycleColumn,
        lifecycleStatusDefaults
      ),
    [columnFilters.activeFilters, lifecycleColumn, lifecycleStatusDefaults]
  );

  const { setFilters: setColumnFilters } = columnFilters;
  const clearColumnFilters = useCallback(
    () => setColumnFilters(getClearedColumnFilters(lifecycleColumn, lifecycleStatusDefaults)),
    [setColumnFilters, lifecycleColumn, lifecycleStatusDefaults]
  );

  const applyViewFiltersToParams = useCallback(
    (params: URLSearchParams, filters: ApiEmployeeFilter) => {
      for (const item of employeesFilterConfig) {
        const key =
          "searchParamName" in item && item.searchParamName ? item.searchParamName : item.name;
        params.delete(key);
      }
      for (const item of employeesFilterConfig) {
        const value = filters[item.name as keyof ApiEmployeeFilter];
        if (value === undefined || value === null) continue;
        const formatted = parseUrlHelper.formatValue(value as never, item.valueType);
        const key =
          "searchParamName" in item && item.searchParamName ? item.searchParamName : item.name;
        if (formatted !== undefined) params.set(key, formatted);
      }

      const migratedColumnFilters = migrateLegacyEmployeeFilters(
        filters as Record<string, unknown>,
        new Set(columnByKey.keys())
      );
      if (migratedColumnFilters.length) {
        params.set(COLUMN_FILTERS_PARAM, JSON.stringify(migratedColumnFilters));
      } else {
        params.delete(COLUMN_FILTERS_PARAM);
      }
    },
    [employeesFilterConfig, columnByKey]
  );

  const applyViewSelection = useCallback(
    (setViewParam: (params: URLSearchParams) => void) => {
      const filterKeys = employeesFilterConfig.map((item) =>
        "searchParamName" in item && item.searchParamName ? item.searchParamName : item.name
      );
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        filterKeys.forEach((key) => next.delete(key));
        // Фильтры по колонкам — часть вьюхи: при смене вьюхи их пишет она сама
        next.delete(COLUMN_FILTERS_PARAM);
        next.delete("page");
        setViewParam(next);
        return next;
      });
      setSearchTerm("");
      setSavedFiltersHydrated(false);
    },
    [employeesFilterConfig, setSearchParams]
  );

  const selectView = useCallback(
    (viewId?: number) =>
      applyViewSelection((next) =>
        viewId == null
          ? next.set(VIEW_PARAM, ALL_EMPLOYEES_PARAM)
          : next.set(VIEW_PARAM, String(viewId))
      ),
    [applyViewSelection]
  );

  const resetToDefaultView = useCallback(
    () => applyViewSelection((next) => next.delete(VIEW_PARAM)),
    [applyViewSelection]
  );

  const switchToSavedView = useCallback(
    (view: ApiEmployeeListView) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("page");
        applyViewFiltersToParams(next, view.value as ApiEmployeeFilter);
        next.set(VIEW_PARAM, String(view.id));
        return next;
      });
      setSearchTerm("");
    },
    [setSearchParams, applyViewFiltersToParams]
  );

  const currentViewFilters = useMemo(
    () => ({
      ...filter,
      employee_lifecycle_statuses: columnFilters.employee_lifecycle_statuses,
      ...columnFilters.requestFilters,
    }),
    [filter, columnFilters.employee_lifecycle_statuses, columnFilters.requestFilters]
  );

  const viewActions = useEmployeeViewActions({
    activeView: viewsNav.activeView,
    currentFilters: currentViewFilters,
    currentColumns,
    defaultColumnIds: defaultVisibleColumns,
    onViewSaved: switchToSavedView,
    onResetToDefault: resetToDefaultView,
  });

  const { setDefaultView } = viewActions;
  useEffect(() => {
    const id = viewsNav.activeViewId;
    if (id == null || id === viewsNav.landingDefaultViewId) return;
    setDefaultView(id);
  }, [viewsNav.activeViewId, viewsNav.landingDefaultViewId, setDefaultView]);

  const initialEmployeeFilters = viewsNav.activeViewFilters;
  const isInitialized = !viewsNav.isLoading;

  const modalsService = useModals();

  const openSaveViewModal = () => {
    modalsService.openModal(SaveViewModal, { onSubmitView: viewActions.saveAsNewView });
  };

  const openManageViewsModal = () => {
    modalsService.openModal(ManageViewsModal, {
      views: viewsNav.views,
      onRename: viewActions.renameView,
      onDelete: viewActions.deleteView,
      onReorder: viewActions.reorderViews,
    });
  };

  const filtersCount = displayedColumnFilters.length;

  const openFiltersModal = () => {
    modalsService.openModal(EmployeeColumnFiltersModal, {
      categories: filterCategories,
      columnByKey,
      filters: columnFilters.filters,
      onApply: columnFilters.setFilters,
      onClearAll: clearColumnFilters,
      lifecycleStatusDefaults,
      renderers: EMPLOYEE_FILTER_RENDERERS,
    });
  };

  const effectiveFilter = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim();

    return {
      ...filter,
      text: normalizedSearchTerm || undefined,
      page_number: canSearch ? filter.page_number : 1,
      // Без списка бэк не отдаст строки таблиц и доп. компенсации; имя, фото и
      // id приходят всегда и в перечислении не нуждаются
      load_columns: buildLoadColumns(visibleColumns),
      // Не передан — статусы организации по умолчанию, `[]` — все статусы
      employee_lifecycle_statuses: columnFilters.employee_lifecycle_statuses,
      // Семь списков по префиксу ключа: ключ в чужом списке бэк отвергает
      ...columnFilters.requestFilters,
    };
  }, [
    filter,
    searchTerm,
    canSearch,
    visibleColumns,
    columnFilters.employee_lifecycle_statuses,
    columnFilters.requestFilters,
  ]);

  const hasActiveFilters = canSearch && savedFiltersHydrated && filtersCount > 0;

  useEffect(() => {
    if (!isInitialized || savedFiltersHydrated || isCatalogLoading) return;

    const migratedUrl = migrateLegacyEmployeeFilterParams(
      searchParams,
      new Set(columnByKey.keys()),
      COLUMN_FILTERS_PARAM
    );
    if (migratedUrl.changed) {
      setSearchParams(migratedUrl.params, { replace: true });
      return;
    }

    const paramsWithoutView = new URLSearchParams(searchParams);
    paramsWithoutView.delete("view");

    if (paramsWithoutView.toString() !== "") {
      // Поиск вьюхи (или ссылки) лежит в URL: поле ввода берёт его оттуда
      const text = searchParams.get("text") ?? "";
      queueMicrotask(() => {
        setSearchTerm(text);
        setSavedFiltersHydrated(true);
      });
      return;
    }

    if (!initialEmployeeFilters) {
      queueMicrotask(() => setSavedFiltersHydrated(true));
      return;
    }

    const next = new URLSearchParams(searchParams);
    applyViewFiltersToParams(next, initialEmployeeFilters);

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
      return;
    }

    queueMicrotask(() => {
      setSearchTerm(initialEmployeeFilters.text ?? "");
      setSavedFiltersHydrated(true);
    });
  }, [
    isInitialized,
    savedFiltersHydrated,
    searchParams,
    initialEmployeeFilters,
    applyViewFiltersToParams,
    setSearchParams,
    isCatalogLoading,
    columnByKey,
  ]);

  useEffect(() => {
    if (!savedFiltersHydrated) return;
    const query = searchParams.toString();
    sessionStorage.setItem(
      EMPLOYEES_LIST_REFERRER_KEY,
      query ? `${window.location.pathname}?${query}` : window.location.pathname
    );
  }, [savedFiltersHydrated, searchParams]);

  const hydratedViewIdRef = useRef(viewsNav.activeViewId);
  useEffect(() => {
    if (hydratedViewIdRef.current === viewsNav.activeViewId) return;
    hydratedViewIdRef.current = viewsNav.activeViewId;
    setSavedFiltersHydrated(false);
  }, [viewsNav.activeViewId]);

  useEffect(() => {
    if (!savedFiltersHydrated || isCatalogLoading || !isFilterReady) return;
    updateFilters(effectiveFilter);
  }, [savedFiltersHydrated, isCatalogLoading, effectiveFilter, updateFilters, isFilterReady]);

  useDebounce(
    () => {
      if (!savedFiltersHydrated) return;
      setSearchParams((currentParams) => {
        const next = new URLSearchParams(currentParams);
        if (searchTerm) {
          next.set("text", searchTerm);
        } else {
          next.delete("text");
        }
        return next;
      });
    },
    300,
    [savedFiltersHydrated, searchTerm]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.employees.viewed);
  });

  const handleExportClick = useCallback(() => {
    const filterToExport = {
      ...effectiveFilter,
      format: "xlsx",
      // Ключи каталога в порядке отображения: выгружаются ровно колонки экрана
      columns: { columns: currentColumns },
    };

    downloadFile({
      apiCall: () => apiEmployees.export(filterToExport),
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.dataExported, {
          [analyticProperties.title]: "Employees",
        });
      },
    });
  }, [effectiveFilter, currentColumns]);

  return (
    <>
      <Page className="h-full">
        <Page.Header
          title={
            isEmployeesLoading
              ? `${t("common:loading")}...`
              : !canSearch || employeesData?.metadata.total_items === 0
                ? t("core:employees")
                : typeof employeesData?.metadata.total_items === "number"
                  ? t("plurals:employees_count", { count: employeesData.metadata.total_items })
                  : "-"
          }
          showBack={false}
        >
          <Stack direction={"horizontal"} gap={"sm"} items={"center"} className={"overflow-hidden"}>
            {canIEdit(ApiPermission.reporting_export) && (
              <DropdownMenu
                trigger={
                  <ButtonIcon
                    variant={"secondary"}
                    icon={<FontAwesomeIcon icon={"fa-light fa-file-export"} />}
                  />
                }
              >
                <DropdownMenuItem
                  onClick={handleExportClick}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-file-xls" />}
                  title={t("common:export_to_xlsx")}
                />
              </DropdownMenu>
            )}
            {canSearch && !viewsNav.isLoading && savedFiltersHydrated && (
              <EmployeeViewSelector
                views={viewsNav.views}
                activeView={viewsNav.activeView}
                isDefaultActive={viewsNav.isDefaultActive}
                isDirty={viewActions.isDirty}
                isUpdating={viewActions.isUpdating}
                onSelectView={selectView}
                onUpdateView={viewActions.updateActiveView}
                onSaveAsNewView={openSaveViewModal}
                onManageViews={openManageViewsModal}
              />
            )}
            {canSearch && !viewsNav.isLoading && savedFiltersHydrated && viewActions.isDirty && (
              <EmployeeSaveViewButton
                isDefaultActive={viewsNav.isDefaultActive}
                isUpdating={viewActions.isUpdating}
                onUpdateView={viewActions.updateActiveView}
                onSaveAsNewView={openSaveViewModal}
              />
            )}
            {columnsButton()}
            {canSearch && savedFiltersHydrated && isCatalogReady && (
              <FilterButton
                count={filtersCount}
                onClick={openFiltersModal}
                onClear={filtersCount ? clearColumnFilters : undefined}
                title={t("common:filter")}
              />
            )}

            {canIEdit(ApiPermission.add_employees) && (
              <HireFlowButton title={t("hiring_flow:add_employee")} />
            )}
          </Stack>
        </Page.Header>
        <Page.Content stretch className="pb-6">
          <Page.StickyLayout
            toolbar={
              canViewEmployeeName ? (
                <Box>
                  <Search
                    placeholder={
                      hasActiveFilters
                        ? t("core:search_personal_data_placeholder")
                        : t("common:search")
                    }
                    value={searchTerm}
                    clearable
                    onClear={() => setSearchTerm("")}
                    onInput={(e) => {
                      setSearchTerm(e.currentTarget.value);
                    }}
                    type={"text"}
                    leadingSlot={
                      hasActiveFilters ? (
                        <EmployeeColumnFiltersChip
                          onOpen={openFiltersModal}
                          filters={displayedColumnFilters}
                          columnByKey={columnByKey}
                          onClear={clearColumnFilters}
                        />
                      ) : undefined
                    }
                  />
                </Box>
              ) : undefined
            }
            footer={
              canSearch ? (
                <PaginationBlock
                  metadata={employeesData?.metadata}
                  onPageChange={(page) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("page", page.toString());
                      return searchParams;
                    });
                  }}
                />
              ) : undefined
            }
          >
            <Table
              data={employeesData?.items || []}
              columns={columns}
              stickyHeader={true}
              stickyFirstColumn={true}
              showSkeleton={isEmployeesLoading}
              showHeaderSkeleton={viewsNav.isLoading}
              sorting={typedFilters.sorting}
              onSortUpdated={onApplySorting}
              onRowClick={(row) => navigate(`/employees/${row.original.id}`)}
            />
          </Page.StickyLayout>
        </Page.Content>
      </Page>
    </>
  );
}
